/**
 * Share Chat API — create share links & fetch public shared snapshots.
 * Security: backend decides access; frontend only displays what the API returns.
 * Do NOT persist shared conversation payloads in localStorage / sessionStorage / IndexedDB.
 */

import { END_POINTS } from "constants/APIConstant";
import { buildPublicShareUrl, extractShareToken } from "./shareUtils";

function getCleanChatbotBaseUrl() {
  if (
    !END_POINTS.CHATBOT_API_BASE_URL ||
    typeof END_POINTS.CHATBOT_API_BASE_URL !== "string"
  ) {
    throw new Error(
      "Invalid CHATBOT_API_BASE_URL: CHATBOT_API_BASE_URL must be a valid string",
    );
  }
  return END_POINTS.CHATBOT_API_BASE_URL.trim().replace(/\/+$/, "");
}

function buildAuthHeaders(token = null, userId = null) {
  const headers = {
    "Content-Type": "application/json",
  };

  if (token != null && token !== "") {
    const tokenString = String(token).trim();
    if (tokenString) headers.authorization = tokenString;
  }

  if (userId != null && userId !== "") {
    const userIdString = String(userId).trim();
    if (userIdString) headers.userId = userIdString;
  }

  for (const [key, value] of Object.entries(headers)) {
    if (typeof value !== "string") {
      throw new Error(
        `Invalid header value for "${key}": must be a string. Got: ${typeof value}`,
      );
    }
    if (value === "") {
      throw new Error(`Invalid header value for "${key}": cannot be empty`);
    }
  }

  return headers;
}

async function parseErrorBody(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function createApiError(response, body) {
  const detail =
    body?.detail || body?.message || body?.error || body?.errorMessage || "";
  const err = new Error(
    detail
      ? `API Error: ${response.status} ${response.statusText} — ${detail}`
      : `API Error: ${response.status} ${response.statusText}`,
  );
  err.status = response.status;
  err.body = body;
  err.code = body?.code || body?.errorCode || null;
  return err;
}

/**
 * POST /api/conversations/{conv_id}/share
 * Returns public frontend share URL ready to copy.
 */
export const createConversationShare = async (
  convId,
  token = null,
  userId = null,
) => {
  const id =
    convId != null && String(convId).trim() !== ""
      ? String(convId).trim()
      : null;

  if (!id) {
    throw new Error("Invalid convId: convId must be a valid non-empty string");
  }

  if (!token || !userId) {
    throw new Error("Authentication required to create a share link");
  }

  const cleanApiBaseUrl = getCleanChatbotBaseUrl();
  const endpoint = `${cleanApiBaseUrl}/api/conversations/${encodeURIComponent(id)}/share`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: buildAuthHeaders(token, userId),
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const body = await parseErrorBody(response);
    throw createApiError(response, body);
  }

  const data = await response.json();
  const shareUrlFromApi = data?.shareUrl || data?.share_url || null;
  const shareToken = extractShareToken(shareUrlFromApi);
  const publicShareUrl = shareToken
    ? buildPublicShareUrl(shareToken)
    : shareUrlFromApi
      ? buildPublicShareUrl(shareUrlFromApi)
      : null;

  return {
    success: Boolean(data?.success ?? true),
    shareUrl: publicShareUrl,
    apiShareUrl: shareUrlFromApi,
    shareToken,
    createdAt: data?.createdAt || data?.created_at || null,
    expiresAt: data?.expiresAt || data?.expires_at || null,
    raw: data,
  };
};

/**
 * GET /api/shared/{token}
 * Public endpoint — no client-side ownership checks.
 * Result is kept in React state only (caller responsibility).
 */
export const getSharedConversation = async (shareToken) => {
  const token =
    shareToken != null && String(shareToken).trim() !== ""
      ? String(shareToken).trim()
      : null;

  if (!token) {
    const err = new Error("Invalid share token");
    err.status = 404;
    err.code = "invalid";
    throw err;
  }

  const cleanApiBaseUrl = getCleanChatbotBaseUrl();
  const endpoint = `${cleanApiBaseUrl}/api/shared/${encodeURIComponent(token)}`;

  let response;
  try {
    response = await fetch(endpoint, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });
  } catch (networkError) {
    const err = new Error(
      networkError?.message || "Network error while loading shared chat",
    );
    err.status = 0;
    err.code = "network";
    throw err;
  }

  if (!response.ok) {
    const body = await parseErrorBody(response);
    throw createApiError(response, body);
  }

  const data = await response.json();
  const conversation = data?.conversation || null;

  return {
    success: Boolean(data?.success ?? true),
    conversation: conversation
      ? {
          title: conversation.title || "",
          messages: Array.isArray(conversation.messages)
            ? conversation.messages
            : [],
          createdAt:
            conversation.createdAt || conversation.created_at || null,
        }
      : null,
    readOnly: data?.readOnly !== false,
    expiresAt: data?.expiresAt || data?.expires_at || null,
    raw: data,
  };
};
